/**
 * Unit conversion utilities for template system
 * Handles conversion between pixels and millimeters for print layouts
 */

// Standard print constants
export const DPI_DEFAULT = 300; // Default DPI for print layouts
export const DPI = DPI_DEFAULT; // Backward compatibility
export const MM_PER_INCH = 25.4; // Millimeters per inch

/**
 * Convert pixels to millimeters
 * @param px - Pixel value
 * @param dpi - Dots per inch (default: 300)
 * @returns Millimeter value
 */
export const pxToMm = (px: number, dpi = DPI_DEFAULT): number => {
  return (px / dpi) * MM_PER_INCH;
};

/**
 * Convert millimeters to pixels
 * @param mm - Millimeter value
 * @param dpi - Dots per inch (default: 300)
 * @returns Pixel value
 */
export const mmToPx = (mm: number, dpi = DPI_DEFAULT): number => {
  return (mm * dpi) / MM_PER_INCH;
};

/**
 * Convert points to pixels
 * @param pt - Point value (1/72 inch)
 * @param dpi - Dots per inch (default: 300)
 * @returns Pixel value
 */
export const ptToPx = (pt: number, dpi = DPI_DEFAULT): number => {
  return (pt * dpi) / 72;
};

/**
 * Convert a bounding box from pixels to millimeters
 * @param bbox - Bounding box in pixels {x, y, width, height}
 * @param dpi - Dots per inch (default: 300)
 * @returns Bounding box in millimeters
 */
export const bboxPxToMm = (
  bbox: { x: number; y: number; width: number; height: number },
  dpi = DPI
) => {
  return {
    x: pxToMm(bbox.x, dpi),
    y: pxToMm(bbox.y, dpi),
    width: pxToMm(bbox.width, dpi),
    height: pxToMm(bbox.height, dpi)
  };
};

/**
 * Convert a bounding box from millimeters to pixels
 * @param bbox - Bounding box in millimeters {x, y, width, height}
 * @param dpi - Dots per inch (default: 300)
 * @returns Bounding box in pixels
 */
export const bboxMmToPx = (
  bbox: { x: number; y: number; width: number; height: number },
  dpi = DPI
) => {
  return {
    x: Math.round(mmToPx(bbox.x, dpi)),
    y: Math.round(mmToPx(bbox.y, dpi)),
    width: Math.round(mmToPx(bbox.width, dpi)),
    height: Math.round(mmToPx(bbox.height, dpi))
  };
};

/**
 * Round millimeter values to reasonable precision
 * @param mm - Millimeter value
 * @param precision - Decimal places (default: 2)
 * @returns Rounded millimeter value
 */
export const roundMm = (mm: number, precision = 2): number => {
  return Math.round(mm * Math.pow(10, precision)) / Math.pow(10, precision);
};

/**
 * Convert and round pixels to millimeters with precision
 * @param px - Pixel value
 * @param dpi - Dots per inch (default: 300)
 * @param precision - Decimal places (default: 2)
 * @returns Rounded millimeter value
 */
export const pxToMmRounded = (px: number, dpi = DPI, precision = 2): number => {
  return roundMm(pxToMm(px, dpi), precision);
};

/**
 * Validate DPI value
 * @param dpi - DPI value to validate
 * @returns True if DPI is valid
 */
export const isValidDpi = (dpi: number): boolean => {
  return dpi > 0 && dpi <= 1200; // Reasonable DPI range
};

/**
 * Get common print DPI values
 * @returns Object with common DPI presets
 */
export const getCommonDpi = () => ({
  web: 72,
  print_draft: 150,
  print_standard: 300,
  print_high: 600
});

/**
 * Log unit conversion for debugging
 * @param px - Pixel value
 * @param dpi - DPI used
 * @param prefix - Log prefix
 */
export const debugConversion = (px: number, dpi = DPI, prefix = '[units]'): void => {
  const mm = pxToMmRounded(px, dpi);
  console.log(`${prefix} ${px}px @ ${dpi}DPI = ${mm}mm`);
};
